import { useEffect, useMemo, useState } from "react"
import { GetApp } from "../App"
import { GetShipContext } from "../App/Context"
import { useSubscription } from "../App/useSubscription"
import { Icons } from "./Icon"
import { MenuItemDividerProps, MenuItemBlank, MenuItemToggleState } from "./Menu"
import { MenuStatusButton, type MenuStatusInfo } from "./MenuStatusButton"
import { ApiClient, NMEAApi, IResponse, ILogbookAppNMEAStatusResult, ILogbookAppNMEAConnectionStatus, INavigationRecordingStatus, ILogbookAppStatusResult } from "../Types"
import { Subscription } from "../Types/Generated/LogbookApi/Subscriptions"
import { GetAppView } from "../Views/AppView"
import { GetShipView } from "../Views/ShipView"
import { ShipViewOverlayContent } from "../Views/ShipOverlayContentView"

export const MenuNMEAData = {
  updateNmea(api: ApiClient, connection: boolean): Promise<IResponse> {
    return new NMEAApi(api).nmea.update({ params: {}, body: { connection: connection } }).call();
  },
};

export interface MenuNMEAProps {
  // Opt-in initial open state, forwarded to the underlying `MenuStatusButton`.
  // Real callers never set this; it exists so catalog fixtures can preview the
  // item list itself, not just the collapsed button (#384).
  initOpen?: boolean;
}

export function MenuNMEA({ initOpen }: MenuNMEAProps) {
  const app = GetApp();
  const { apiUser } = GetShipContext();
  const nmeaUpdateAvailable = new NMEAApi(app.apiClient).nmea.update({ params: {}, body: {} }).available(apiUser);
  const { subscriptionExpired } = GetAppView();
  const { setShipOverlay } = GetShipView();
  const [nmeaStatus, setNmeaStatus] = useState<ILogbookAppNMEAStatusResult | undefined>(app.status?.nmea);
  const t = app.getT("MenuNMEA");

  useEffect(() => {
    if (app.status && app.status.nmea) {
      setNmeaStatus(app.status.nmea);
    }
  }, [app.status]);

  useSubscription((status: ILogbookAppStatusResult, _type: string) => {
    if (status.nmea) {
      setNmeaStatus(status.nmea);
    }
  }, Subscription.AppStatus);

  const nmeaConnectionClass = useMemo(() => {
    if (nmeaStatus?.connected) {
      return "ok";
    } else if (nmeaStatus?.connectionStatus != ILogbookAppNMEAConnectionStatus.notConfigured) {
      return "neutral";
    }
    return "error";
  }, [nmeaStatus]);

  const nmeaRecordingClass = useMemo(() => {
    if (nmeaStatus?.connectionStatus == ILogbookAppNMEAConnectionStatus.notConfigured || !nmeaStatus?.connected) {
      return "";
    }
    if (nmeaStatus?.recordingStatus == INavigationRecordingStatus.recording) {
      return "ok";
    } else if (nmeaStatus?.recordingStatus == INavigationRecordingStatus.paused) {
      return "neutral";
    }
    return "";
  }, [nmeaStatus]);

  const nmeaMenuInfo = useMemo<MenuStatusInfo | undefined>(() => {
    if (app.cloudSession()) {
      return undefined;
    }
    if (nmeaStatus?.connected) {
      if (nmeaStatus.recordingStatus != INavigationRecordingStatus.recording) {
        return { icon: Icons.Box, class: "ShipView-status-ok", surface: "outlined" };
      }
      return { icon: Icons.BoxFilled, class: "ShipView-status-ok", surface: "filled" };
    } else if (nmeaStatus?.connectionStatus != ILogbookAppNMEAConnectionStatus.notConfigured) {
      return { icon: Icons.Box, class: "ShipView-status-neutral", surface: "outlined" };
    }
    return { icon: Icons.Box, class: "ShipView-status-error", surface: "outlined" };
  }, [nmeaStatus]);

  const nmeaConnectionToggleStatus = useMemo(() => {
    if (nmeaStatus?.connectionStatus == ILogbookAppNMEAConnectionStatus.notConfigured) {
      return MenuItemToggleState.disabled;
    } else if (nmeaStatus?.connected && nmeaStatus?.recordingStatus) {
      return MenuItemToggleState.on;
    }
    return MenuItemToggleState.off;
  }, [nmeaStatus]);

  if (app.cloudSession() || !nmeaStatus || !nmeaMenuInfo) {
    return;
  }

  return (
    <MenuStatusButton
      info={nmeaMenuInfo}
      title={t("title")}
      buttonClass="button"
      disabled={subscriptionExpired}
      global={true}
      initOpen={initOpen}
      items={[
        {
          disabled: !nmeaStatus || nmeaStatus.connectionStatus == ILogbookAppNMEAConnectionStatus.notConfigured,
          id: "nmea-connection",
          onClick: nmeaUpdateAvailable ? async () => {
            const _response = await MenuNMEAData.updateNmea(app.apiClient, !nmeaStatus.connected);
            return true;
          } : undefined,
          icon: Icons.CircleFilledLarge,
          iconClass: `ShipView-status-${nmeaConnectionClass}`,
          title: t("connection"),
          toggle: nmeaUpdateAvailable ? nmeaConnectionToggleStatus : undefined,
        },

        // Option nmea-recording
        {
          disabled: nmeaStatus?.connectionStatus == ILogbookAppNMEAConnectionStatus.notConfigured || !nmeaStatus?.connected,
          id: "nmea-recording",
          icon: Icons.CircleFilledLarge,
          iconClass: `ShipView-status-${nmeaRecordingClass}`,
          title: t("recording"),
        },

        nmeaStatus.lastReceived ? { id: "nmea-last-update", title: t("last_update", { timestamp: app.format("timeAgoDate", nmeaStatus.lastReceived) }) } : MenuItemBlank,

        ...(nmeaUpdateAvailable ? [
          MenuItemDividerProps,

          // Option nmea-settings
          { id: "nmea-settings", icon: Icons.Settings, title: t("settings"), onClick: () => { setShipOverlay(ShipViewOverlayContent.nmeaSettings); return true; } },
        ] : []),
      ].filter((item) => item)}
    />
  );
}
